/**
 * Color Drop Zone Element Editor
 * Background image, hotspot zones, draggable objects with color palette
 */

import * as React from 'react';
import {
    CardTitle,
    Button,
    Badge,
    cn,
    Input,
    Label,
    Textarea,
    DndContext,
    useDraggable,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    type DragEndEvent,
} from '@ai-content-hub/content-kit';
import { Plus, Trash2, ChevronDown, ChevronRight, ImagePlus, ImageMinus, GripHorizontal } from 'lucide-react';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import {
    ColorPalette,
    ColorPaletteEditor,
    ImageDropCanvas,
    useCanvasSize,
    getCanvasScale,
} from '@ai-content-hub/content-kit';
import type { BlockContent } from '../block';
import { BlockEditor, BlockRenderer, createDefaultBlockContent } from '../block';
import {
    type ColorDropZoneElementData,
    type DropZoneHotspot,
    type DropZoneObject,
    normalizeColorDropZoneData,
    createDefaultHotspot,
    createDefaultDropZoneObject,
    getBackgroundImageSrc,
    colorDropZoneElementSchema,
} from './schema';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

const COLOR_DROP_ZONE_ALLOWED_TYPES = ['image'] as const;
const MIN_OBJECTS = 1;
const MAX_OBJECTS = 20;
const MIN_COLORS = 2;
const MAX_COLORS = 12;
const RADIUS_MIN = 20;
const RADIUS_MAX = 200;

const EDITOR_DRAGGABLE_PREFIX = 'editor-zone-';

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function AdvancedObjectSection({
    obj,
    updateObject,
    disabled,
    t,
}: {
    obj: DropZoneObject;
    updateObject: (id: string, updates: Partial<DropZoneObject>) => void;
    disabled: boolean;
    t: (key: string, fallback?: string) => string;
}) {
    const [open, setOpen] = React.useState(!!obj.explanation?.trim());
    return (
        <div className="border-t border-border pt-3 mt-3">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
                {open ? (
                    <ChevronDown className="h-4 w-4" />
                ) : (
                    <ChevronRight className="h-4 w-4" />
                )}
                {t('cdzAdvancedToggle')}
            </button>
            {open && (
                <div className="mt-2">
                    <Label className="text-sm font-medium">
                        {t('cdzExplanationFeedbackLabel')}
                    </Label>
                    <Textarea
                        value={obj.explanation ?? ''}
                        onChange={(e) =>
                            updateObject(obj.id, { explanation: e.target.value })
                        }
                        placeholder={t('cdzExplanationPlaceholder')}
                        className="mt-1 min-h-[60px]"
                        disabled={disabled}
                    />
                </div>
            )}
        </div>
    );
}

function DraggableZoneNode({
    zone,
    obj,
    colorPalette,
    updateZone,
    disabled,
    isSelected,
    onSelect,
    containerRef,
    t,
}: {
    zone: DropZoneHotspot;
    obj: DropZoneObject;
    colorPalette: { id: string; label?: string; value: string }[];
    updateZone: (zoneId: string, updates: Partial<DropZoneHotspot>) => void;
    disabled: boolean;
    isSelected: boolean;
    onSelect: (objId: string) => void;
    containerRef: React.RefObject<HTMLDivElement | null>;
    t: (key: string, fallback?: string) => string;
}) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `${EDITOR_DRAGGABLE_PREFIX}${zone.id}`,
        disabled,
    });

    const canvasSize = useCanvasSize();
    const opacity = obj.opacity ?? 1;
    const radius = clamp(zone.radius, RADIUS_MIN, RADIUS_MAX);
    const baseDiameter = radius * 2;
    const size = baseDiameter * getCanvasScale(canvasSize);
    const style: React.CSSProperties = {
        position: 'absolute',
        left: `${zone.x}%`,
        top: `${zone.y}%`,
        transform: transform
            ? `translate(${transform.x}px, ${transform.y}px) translate(-50%, -50%)`
            : 'translate(-50%, -50%)',
        width: size,
        height: size,
        cursor: disabled ? 'default' : 'grab',
        opacity: isDragging ? 0.8 : opacity,
        zIndex: isDragging ? 50 : isSelected ? 10 : 1,
    };

    const handleResizePointerDown = React.useCallback(
        (e: React.PointerEvent) => {
            e.stopPropagation();
            if (disabled || !containerRef.current) return;

            const handleMove = (moveEvent: PointerEvent) => {
                moveEvent.preventDefault();
                const rect = containerRef.current?.getBoundingClientRect();
                if (!rect) return;
                const imgW = canvasSize?.width ?? rect.width;
                const imgH = canvasSize?.height ?? rect.height;
                const imgLeft = rect.left + (canvasSize?.left ?? 0);
                const imgTop = rect.top + (canvasSize?.top ?? 0);
                const centerX = imgLeft + (zone.x / 100) * imgW;
                const centerY = imgTop + (zone.y / 100) * imgH;
                const dx = moveEvent.clientX - centerX;
                const dy = moveEvent.clientY - centerY;
                const distPx = Math.sqrt(dx * dx + dy * dy);
                const scale = getCanvasScale(canvasSize);
                const newRadius =
                    scale > 0
                        ? clamp(
                            Math.round(distPx / scale),
                            RADIUS_MIN,
                            RADIUS_MAX
                        )
                        : zone.radius;
                if (newRadius !== zone.radius) {
                    updateZone(zone.id, { radius: newRadius });
                }
            };

            const handleUp = () => {
                window.removeEventListener('pointermove', handleMove);
                window.removeEventListener('pointerup', handleUp);
                (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            };

            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            window.addEventListener('pointermove', handleMove);
            window.addEventListener('pointerup', handleUp);
        },
        [containerRef, canvasSize, disabled, zone, updateZone]
    );

    const handleClick = React.useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!disabled) onSelect(obj.id);
        },
        [disabled, obj.id, onSelect]
    );

    const correctColorHex = obj.correctColorId
        ? colorPalette.find((c) => c.id === obj.correctColorId)?.value
        : undefined;

    // Memoize the image content so it doesn't re-render/re-fetch on resize/drag
    const renderedContent = React.useMemo(
        () => <BlockRenderer content={obj.content} compact className="w-full h-full" />,
        [obj.content]
    );

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={handleClick}
            className={cn(
                'relative flex flex-col items-center justify-center rounded border-2 overflow-hidden',
                'border-primary bg-primary/10',
                isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                !disabled && 'active:cursor-grabbing'
            )}
        >
            <div className="absolute top-0 left-0 right-0 px-1.5 py-0.5 bg-primary/90 text-primary-foreground text-xs font-medium truncate text-center z-[2]">
                {obj.name || t('cdzObjectFallbackName')}
            </div>
            <div className="relative flex-1 w-full min-h-0 flex items-center justify-center isolate">
                {correctColorHex && (
                    <svg width="0" height="0" className="absolute">
                        <defs>
                            <filter id={`edit-tint-${obj.id}`} colorInterpolationFilters="sRGB">
                                <feFlood floodColor={correctColorHex} result="color" />
                                <feBlend in="SourceGraphic" in2="color" mode="multiply" />
                                <feComposite in2="SourceGraphic" operator="in" />
                            </filter>
                        </defs>
                    </svg>
                )}
                <div
                    style={correctColorHex ? { filter: `url(#edit-tint-${obj.id})` } : undefined}
                    className="w-full h-full flex items-center justify-center"
                >
                    {renderedContent}
                </div>
            </div>
            {!disabled && (
                <div
                    role="button"
                    tabIndex={0}
                    onPointerDown={handleResizePointerDown}
                    className="absolute bottom-0 right-0 w-5 h-5 flex items-center justify-center bg-primary/80 text-primary-foreground rounded-tl cursor-se-resize hover:bg-primary z-[2]"
                    aria-label={t('cdzResizeHotspotAria')}
                >
                    <GripHorizontal className="h-3 w-3" />
                </div>
            )}
        </div>
    );
}


export interface ColorDropZoneEditProps extends ElementEditorProps<ColorDropZoneElementData> { }

export const ColorDropZoneEdit: React.FC<ColorDropZoneEditProps> = ({
    value,
    onChange,
    disabled = false,
    className,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? colorDropZoneElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const data = React.useMemo(
        () => normalizeColorDropZoneData(value ?? {}),
        [value]
    );

    const updateData = (updates: Partial<ColorDropZoneElementData>) => {
        onChange({ ...data, ...updates } as ColorDropZoneElementData);
    };

    const updateZone = (zoneId: string, updates: Partial<DropZoneHotspot>) => {
        if (disabled) return;
        updateData({
            zones: data.zones.map((z) =>
                z.id === zoneId ? { ...z, ...updates } : z
            ),
        });
    };

    const bgSrc = getBackgroundImageSrc(data.background);

    const addObject = () => {
        if (disabled || data.objects.length >= MAX_OBJECTS) return;
        if (!bgSrc) return;
        const zoneId = `zone-${Date.now()}`;
        const objId = `obj-${Date.now() + 1}`;
        const idx = data.objects.length;
        const newZone = {
            ...createDefaultHotspot(zoneId, {
                label: `Zone ${idx + 1}`,
                linkedObjectId: objId,
            }),
            x: 50 + ((idx % 3) - 1) * 15,
            y: 30 + Math.floor(idx / 3) * 25,
        };
        const newObj = {
            ...createDefaultDropZoneObject(objId),
            correctZoneId: zoneId,
            opacity: 1,
        };
        updateData({
            zones: [...data.zones, newZone],
            objects: [...data.objects, newObj],
        });
    };

    const removeObject = (objectId: string) => {
        if (disabled || data.objects.length <= MIN_OBJECTS) return;
        const obj = data.objects.find((o) => o.id === objectId);
        const linkedZoneId = obj?.correctZoneId;
        const othersUsingZone = data.objects.filter(
            (o) => o.id !== objectId && o.correctZoneId === linkedZoneId
        );
        updateData({
            objects: data.objects.filter((o) => o.id !== objectId),
            zones:
                linkedZoneId && othersUsingZone.length === 0
                    ? data.zones.filter((z) => z.id !== linkedZoneId)
                    : data.zones,
        });
    };

    const updateObject = (objectId: string, updates: Partial<DropZoneObject>) => {
        if (disabled) return;
        updateData({
            objects: data.objects.map((o) =>
                o.id === objectId ? { ...o, ...updates } : o
            ),
        });
    };

    const handleBackgroundChange = (content: BlockContent) => {
        updateData({ background: content });
    };

    const handleRemoveBackground = () => {
        updateData({ background: createDefaultBlockContent('image') });
    };

    const [selectedObjectId, setSelectedObjectId] = React.useState<string | null>(null);
    const [changeBgDialogOpen, setChangeBgDialogOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const selectedObject = selectedObjectId
        ? data.objects.find((o) => o.id === selectedObjectId)
        : undefined;

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setSelectedObjectId(null);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, delta } = event;
            if (!containerRef.current || disabled) return;
            const id = String(active.id);
            if (!id.startsWith(EDITOR_DRAGGABLE_PREFIX)) return;
            const zoneId = id.slice(EDITOR_DRAGGABLE_PREFIX.length);
            const zone = data.zones.find((z) => z.id === zoneId);
            if (!zone) return;
            if (Math.abs(delta.x) < 5 && Math.abs(delta.y) < 5) {
                const linkedObj = data.objects.find((o) => o.correctZoneId === zoneId);
                if (linkedObj) setSelectedObjectId(linkedObj.id);
                return;
            }
            const rect = containerRef.current.getBoundingClientRect();
            const deltaXPercent = (delta.x / rect.width) * 100;
            const deltaYPercent = (delta.y / rect.height) * 100;
            updateZone(zoneId, {
                x: clamp(zone.x + deltaXPercent, 0, 100),
                y: clamp(zone.y + deltaYPercent, 0, 100),
            });
        },
        [data.zones, data.objects, disabled, updateZone]
    );

    const handleCanvasClick = React.useCallback(() => {
        setSelectedObjectId(null);
    }, []);
    const colorPaletteSwatches = data.colorPalette.map((c) => ({
        id: c.id,
        label: c.label || c.value,
        value: c.value,
    }));

    return (
        <div className={cn('color-drop-zone-edit space-y-6', className)}>
            {/* Canvas with Toolbar */}
            <div className="color-drop-zone-edit-canvas space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="text-lg font-semibold">
                        {t('cdzCanvas')}
                    </CardTitle>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex flex-col items-start">
                            <Button
                                type="button"
                                variant={!bgSrc ? 'destructive' : 'outline'}
                                size="sm"
                                onClick={() => setChangeBgDialogOpen(true)}
                                disabled={disabled}
                                className={cn(!bgSrc && 'animate-pulse')}
                            >
                                <ImagePlus className="h-4 w-4 mr-1" />
                                {t('cdzChangeBackground')}
                            </Button>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveBackground}
                            disabled={disabled || !bgSrc}
                        >
                            <ImageMinus className="h-4 w-4 mr-1" />
                            {t('cdzRemoveBackground')}
                        </Button>
                        {selectedObject && (
                            <>
                                <div className="flex items-center gap-2 border-l border-border pl-2">
                                    <Label className="text-sm shrink-0">
                                        {t('cdzOpacity')}
                                    </Label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        value={Math.round((selectedObject.opacity ?? 1) * 100)}
                                        onChange={(e) =>
                                            updateObject(selectedObject.id, {
                                                opacity: Number(e.target.value) / 100,
                                            })
                                        }
                                        disabled={disabled}
                                        className="w-20 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                                    />
                                    <span className="text-xs text-muted-foreground w-8">
                                        {Math.round((selectedObject.opacity ?? 1) * 100)}%
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 border-l border-border pl-2">
                                    <Label className="text-sm shrink-0">
                                        {t('cdzToolbarColor')}
                                    </Label>
                                    <ColorPalette
                                        options={colorPaletteSwatches}
                                        selectedId={selectedObject.correctColorId}
                                        onSelect={(id) =>
                                            updateObject(selectedObject.id, {
                                                correctColorId: id,
                                            })
                                        }
                                        disabled={disabled}
                                        size="sm"
                                    />
                                </div>
                            </>
                        )}
                        <Badge
                            variant="secondary"
                            className="bg-info/10 text-info shrink-0"
                        >
                            {data.zones.length} {t('cdzZonesLabel')}
                        </Badge>
                    </div>
                </div>
                {bgSrc ? (
                    <DndContext onDragEnd={handleDragEnd}>
                        <div
                            ref={containerRef}
                            className="relative cursor-default"
                            onClick={handleCanvasClick}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Escape' && setSelectedObjectId(null)}
                        >
                            <ImageDropCanvas
                                src={bgSrc}
                                alt={t('cdzBackgroundAlt')}
                                className="rounded-sm overflow-hidden border border-border"
                                fitViewport
                                translate={t}
                            >
                                {data.zones.map((zone) => {
                                    const linkedObj = data.objects.find(
                                        (o) => o.correctZoneId === zone.id
                                    );
                                    return linkedObj ? (
                                        <DraggableZoneNode
                                            key={zone.id}
                                            zone={zone}
                                            obj={linkedObj}
                                            colorPalette={colorPaletteSwatches}
                                            updateZone={updateZone}
                                            disabled={disabled}
                                            isSelected={selectedObjectId === linkedObj.id}
                                            onSelect={setSelectedObjectId}
                                            containerRef={containerRef}
                                            t={t}
                                        />
                                    ) : (
                                        <div
                                            key={zone.id}
                                            className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-primary bg-primary/30 pointer-events-none"
                                            style={{
                                                left: `${zone.x}%`,
                                                top: `${zone.y}%`,
                                            }}
                                        />
                                    );
                                })}
                            </ImageDropCanvas>
                        </div>
                    </DndContext>
                ) : (
                    <>
                    <div
                        className="rounded-sm border border-dashed border-border bg-muted/20 p-6 text-center text-muted-foreground cursor-pointer"
                        onClick={() => !disabled && setChangeBgDialogOpen(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                            (e.key === 'Enter' || e.key === ' ') &&
                            !disabled &&
                            setChangeBgDialogOpen(true)
                        }
                    >
                        <ImagePlus className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                        <p className="text-sm">{t('cdzEmptyCanvasHint')}</p>
                    </div>
                    <p className="color-drop-zone-edit-bg-error text-sm text-destructive mt-2">
                        {t('cdzBgRequiredWarning')}
                    </p>
                    </>
                )}
            </div>

            {/* Change Background Dialog */}
            <Dialog open={changeBgDialogOpen} onOpenChange={setChangeBgDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('cdzDialogChangeBackgroundTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="rounded-sm border border-dashed border-border bg-muted/20 p-4">
                        <BlockEditor
                            content={data.background}
                            onChange={handleBackgroundChange}
                            disabled={disabled}
                            compact={false}
                            allowedTypes={['image']}
                            imageConfig={{
                                hiddenFields: ['caption', 'size', 'alignment'],
                                defaults: { size: 'full', alignment: 'center' },
                                borderless: true,
                            }}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            {/* Color Palette */}
            <div className="color-drop-zone-edit-palette space-y-2">
                <ColorPaletteEditor
                    value={colorPaletteSwatches}
                    onChange={(opts) =>
                        updateData({
                            colorPalette: opts.map((o) => ({
                                id: o.id,
                                label: o.label,
                                value: o.value,
                            })),
                        })
                    }
                    minItems={MIN_COLORS}
                    maxItems={MAX_COLORS}
                    translate={t}
                />
            </div>

            {/* Objects */}
            <div className="color-drop-zone-edit-objects space-y-4">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                        {t('cdzObjectsHeading')}
                    </CardTitle>
                    <Badge
                        variant="secondary"
                        className="bg-success/10 text-success shrink-0"
                    >
                        {data.objects.length}
                    </Badge>
                </div>
                <div className="space-y-4">
                    {data.objects.map((obj, idx) => (
                        <div
                            key={obj.id}
                            className="rounded-sm border bg-muted/30 p-4 space-y-3"
                        >
                            <div className="flex items-start gap-3">
                                <Badge
                                    variant="secondary"
                                    className="bg-success/10 text-success shrink-0 w-9 justify-center"
                                >
                                    {idx + 1}
                                </Badge>
                                <div className="flex-1 min-w-0 space-y-3">
                                    <div>
                                        <Label className="text-sm font-medium">
                                            {t('cdzObjectNameLabel')}
                                        </Label>
                                        <Input
                                            value={obj.name}
                                            onChange={(e) =>
                                                updateObject(obj.id, {
                                                    name: e.target.value,
                                                })
                                            }
                                            placeholder={t('cdzObjectNamePlaceholder')}
                                            className="mt-1"
                                            disabled={disabled}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-sm font-medium">
                                            {t('content')}
                                        </Label>
                                        <div className="mt-1">
                                            <BlockEditor
                                                content={obj.content}
                                                onChange={(c) =>
                                                    updateObject(obj.id, {
                                                        content: c,
                                                    })
                                                }
                                                disabled={disabled}
                                                compact={true}
                                                allowedTypes={[
                                                    ...COLOR_DROP_ZONE_ALLOWED_TYPES,
                                                ]}
                                                imageConfig={{
                                                    hiddenFields: ['caption', 'size', 'alignment'],
                                                    defaults: { size: 'full', alignment: 'center' },
                                                    borderless: true,
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-sm font-medium">
                                            {t('cdzCorrectColorLabel')}
                                        </Label>
                                        <div className="mt-1">
                                            <ColorPalette
                                                options={colorPaletteSwatches}
                                                selectedId={obj.correctColorId}
                                                onSelect={(id) =>
                                                    updateObject(obj.id, {
                                                        correctColorId: id,
                                                    })
                                                }
                                                disabled={disabled}
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {t('cdzClickCanvasForOpacityHint')}
                                    </div>
                                    <AdvancedObjectSection
                                        obj={obj}
                                        updateObject={updateObject}
                                        disabled={disabled}
                                        t={t}
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/5 shrink-0"
                                    onClick={() => removeObject(obj.id)}
                                    disabled={
                                        disabled ||
                                        data.objects.length <= MIN_OBJECTS
                                    }
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className="w-full border-2 border-dashed border-cta/50"
                        onClick={addObject}
                        disabled={
                            disabled ||
                            data.objects.length >= MAX_OBJECTS ||
                            !bgSrc
                        }
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        {t('cdzAddObject')}
                    </Button>
                    
                </div>
            </div>
        </div>
    );
};

export default ColorDropZoneEdit;
